import * as helios from "@hyperionbt/helios";
import { KreateUTxO } from "kreate-common/modules/business-types";
import { CLIENT_ENV } from "kreate-env/client";

import ScriptOffer from "../../onchain/scriptOffer.hl";
import {
  addHeliosCollateral,
  getCardanoNetworkParams,
  toHeliosDatum,
  toHeliosUTxO,
} from "../../utils";

// Roughly min lovelace in a UTxO
const MIN_ROYALTY = 1_000_000;

// TODO: Support collection offers
export async function buildTx$AcceptSecondaryOffer(
  wallet: helios.Cip30Wallet,
  utxoOffer: KreateUTxO,
  acceptQuantity: number,
  treasuryAddress: helios.Address,
  txMessage: string
) {
  const walletHelper = new helios.WalletHelper(wallet);
  const address = await walletHelper.baseAddress;

  if (utxoOffer.datum.type !== "offer") {
    throw new Error("wrong datum type for offer UTxO");
  }
  const datumOffer = utxoOffer.datum.value;

  const tx = new helios.Tx();
  await addHeliosCollateral(wallet, tx);

  const policyId = new helios.MintingPolicyHash(
    datumOffer.assetId.substring(0, 56)
  );
  const assetNameBytes = helios.hexToBytes(datumOffer.assetId.substring(56));
  const fulfillingAssets = new helios.Assets([
    [policyId, [[assetNameBytes, BigInt(acceptQuantity)]]],
  ]);
  const [utxos, remainingUtxos] = await walletHelper.pickUtxos(
    new helios.Value(BigInt(0), fulfillingAssets),
    helios.CoinSelection.selectLargestFirst
  );
  tx.addInputs(utxos);

  const offerHeliosUTxO = toHeliosUTxO(utxoOffer);
  tx.addInput(offerHeliosUTxO, new helios.ConstrData(0, []));

  const scriptOffer = new ScriptOffer({
    TREASURY_ADDRESS: treasuryAddress,
  }).compile(true);
  tx.attachScript(scriptOffer);

  const scriptOfferAddress = helios.Address.fromValidatorHash(
    scriptOffer.validatorHash,
    new helios.StakeKeyHash(
      helios.hexToBytes(CLIENT_ENV.SCRIPT_STAKE_KEY_HASH)
    ),
    CLIENT_ENV.CARDANO_NETWORK !== "Mainnet"
  );

  const saleAmount = datumOffer.editionPrice * acceptQuantity;

  if (acceptQuantity < datumOffer.wantedQuantity) {
    tx.addOutput(
      new helios.TxOutput(
        scriptOfferAddress,
        new helios.Value(BigInt(utxoOffer.lovelaceValue - saleAmount)),
        toHeliosDatum({
          type: "offer",
          value: {
            ...datumOffer,
            wantedQuantity: datumOffer.wantedQuantity - acceptQuantity,
          },
        })
      )
    );
  }

  const offererAddress = new helios.Address(datumOffer.offererAddress);
  tx.addOutput(
    new helios.TxOutput(
      offererAddress,
      new helios.Value(BigInt(0), fulfillingAssets)
    )
  );

  const protocolFee = Math.max(
    Math.floor((saleAmount * CLIENT_ENV.KOLLECTOR_MINT_FEE_PERCENTAGE) / 100),
    CLIENT_ENV.MIN_PROTOCOL_FEE
  );
  tx.addOutput(
    new helios.TxOutput(treasuryAddress, new helios.Value(BigInt(protocolFee)))
  );

  let royaltyFee = 0;
  if (datumOffer.artistAddress != null && datumOffer.editionRoyalty > 0) {
    royaltyFee = Math.max(
      datumOffer.editionRoyalty * acceptQuantity,
      MIN_ROYALTY
    );
    tx.addOutput(
      new helios.TxOutput(
        new helios.Address(datumOffer.artistAddress),
        new helios.Value(BigInt(royaltyFee))
      )
    );
  }

  /** NOTE: @thanh-kreate
   * Since helios api does not automatically break a string that is less than 64
   * characters to array, this should be done manually to satisfy CIP-20 constraints
   * More details: https://cips.cardano.org/cips/cip20/
   * */
  tx.addMetadata(674, {
    map: [
      [
        "msg",
        helios.textToBytes(txMessage).length <= 64 ? [txMessage] : txMessage,
      ],
    ],
  });

  await tx.finalize(
    getCardanoNetworkParams(CLIENT_ENV.CARDANO_NETWORK),
    address,
    remainingUtxos
  );

  // Min ADA paid to the offerer to store the fulfilled tokens
  const minAdaFee = Number(
    tx.body.outputs.find((o) => o.address === offererAddress)?.value.lovelace ??
      BigInt(0)
  );
  const txFee = Number(tx.body.fee);

  return { tx, saleAmount, royaltyFee, protocolFee, minAdaFee, txFee };
}
